<template>
  <section>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div>
        <h5 class="mb-0">{{ t("ecolabels.duplicates.title") }}</h5>
        <p class="text-muted mb-0 small">
          {{ t("ecolabels.duplicates.subtitle", { pending: total }) }}
        </p>
      </div>
      <div class="d-flex gap-2 align-items-center">
        <select
          v-model="statusFilter"
          class="form-select form-select-sm"
          style="width: auto"
          @change="reload"
        >
          <option value="PENDING">
            {{ t("ecolabels.duplicates.filter_pending") }}
          </option>
          <option value="NOT_DUPLICATE">
            {{ t("ecolabels.duplicates.statuses.not_duplicate") }}
          </option>
          <option value="MERGED">
            {{ t("ecolabels.duplicates.statuses.merged") }}
          </option>
        </select>
        <button
          class="btn btn-outline-primary btn-sm"
          :disabled="scanning"
          @click="triggerScan"
        >
          <span v-if="scanning" class="spinner-border spinner-border-sm me-1" />
          <font-awesome-icon v-else icon="magnifying-glass" class="me-1" />
          {{ t("ecolabels.duplicates.scan") }}
        </button>
      </div>
    </div>

    <LoadingIndicatorComponent v-if="loading" />

    <div
      v-else-if="!pairs.length"
      class="text-center text-muted py-5 border rounded"
    >
      <font-awesome-icon
        icon="check-circle"
        size="2x"
        class="mb-2 text-success"
      />
      <p class="mb-0">{{ t("ecolabels.duplicates.empty") }}</p>
    </div>

    <template v-else>
      <DuplicatePairCard
        v-for="pair in pairs"
        :key="pair.id"
        :pair="pair"
        @resolved="onResolved"
      />
      <div v-if="pairs.length < total" class="text-center my-3">
        <button
          class="btn btn-outline-secondary btn-sm"
          :disabled="loadingMore"
          @click="loadMore"
        >
          <span
            v-if="loadingMore"
            class="spinner-border spinner-border-sm me-1"
          />
          {{
            t("ecolabels.duplicates.load_more", { shown: pairs.length, total })
          }}
        </button>
      </div>
    </template>
  </section>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { ecolabelsApi } from "../api/ecolabelsApi";
import type { DuplicatePair } from "../api/ecolabelsTypes";
import DuplicatePairCard from "./DuplicatePairCard.vue";

const PAGE_SIZE = 25;

const emit = defineEmits<{
  "devices-changed": [];
}>();

const { t } = useI18n();
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();

const loading = ref(true);
const loadingMore = ref(false);
const scanning = ref(false);
const pairs = ref<DuplicatePair[]>([]);
const total = ref(0);
const statusFilter = ref<"PENDING" | "NOT_DUPLICATE" | "MERGED">("PENDING");

onMounted(reload);

async function reload() {
  loading.value = true;
  try {
    const response = await ecolabelsApi.listDuplicatePairs(
      statusFilter.value,
      0,
      PAGE_SIZE
    );
    pairs.value = response.data.pairs;
    total.value = response.data.total;
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    loading.value = false;
  }
}

async function loadMore() {
  loadingMore.value = true;
  try {
    const response = await ecolabelsApi.listDuplicatePairs(
      statusFilter.value,
      pairs.value.length,
      PAGE_SIZE
    );
    pairs.value = [...pairs.value, ...response.data.pairs];
    total.value = response.data.total;
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    loadingMore.value = false;
  }
}

async function triggerScan() {
  scanning.value = true;
  try {
    await ecolabelsApi.triggerDuplicateScan();
    globalSuccessHandler(t("ecolabels.duplicates.scan_started"), "");
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    scanning.value = false;
  }
}

function onResolved(pair: DuplicatePair) {
  // Resolved pairs leave the PENDING queue immediately; merges also change the catalog.
  pairs.value = pairs.value.filter((p) => p.id !== pair.id);
  total.value = Math.max(0, total.value - 1);
  if (pair.status === "MERGED" || pairs.value.length === 0) {
    emit("devices-changed");
  }
}
</script>
